import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Card, Badge, Spinner, Input, Select, Button, cx } from "../components/ui";
import { fmtDateTime } from "../lib/format";
import { ColumnFilter } from "../components/ColumnFilter";

/** Plain-language names for the diff-derived "what changed" kinds. */
const CHANGE_LABELS: Record<string, string> = {
  create: "create",
  reactivate: "reactivate",
  discontinue: "discontinue",
  price: "sales price",
  purchase: "purchase price",
  description: "description",
  visibility: "visibility (shop)",
  stock: "stock (shop)",
  name: "product name (shop)",
  ean: "barcode (shop)",
  brand: "brand (shop)",
  category: "category (shop)",
  weight: "weight (shop)",
  supplier: "supplier link",
};

export default function Activity() {
  const [tab, setTab] = useState<"audit" | "api">("audit");
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Activity log</h1>
        <p className="text-sm text-slate-500">Every transaction and external API call is recorded here.</p>
      </div>
      <div className="flex rounded-lg border border-slate-300 bg-white p-0.5 w-fit">
        <button onClick={() => setTab("audit")} className={cx("rounded-md px-4 py-1.5 text-sm", tab === "audit" ? "bg-indigo-600 text-white" : "text-slate-600")}>Transactions</button>
        <button onClick={() => setTab("api")} className={cx("rounded-md px-4 py-1.5 text-sm", tab === "api" ? "bg-indigo-600 text-white" : "text-slate-600")}>API calls</button>
      </div>
      {tab === "audit" ? <AuditTab /> : <ApiTab />}
    </div>
  );
}

const asObj = (v: unknown): Record<string, unknown> => {
  if (v && typeof v === "object") return v as Record<string, unknown>;
  if (typeof v === "string") { try { return JSON.parse(v) as Record<string, unknown>; } catch { return {}; } }
  return {};
};
const money = (n: unknown) => (typeof n === "number" ? `€${n.toFixed(2)}` : String(n ?? "—"));

/** One-line "what changed" from an op diff — shared by the audit list and the SKU history modal. */
function summarizeDiff(diff: unknown, opType?: string | null): string {
  const d = asObj(diff) as { kind?: string; action?: string; fields?: string[]; field?: string; from?: unknown; to?: unknown; deltaPct?: number | null; tracking?: string; sales?: { from: number; to: number }; purchase?: { from: number; to: number }; itemCode?: string; reason?: string; changes?: { field?: string; from?: unknown; to?: unknown; deltaPct?: number | null; reason?: string; tracking?: string }[] };
  if (opType === "create" || d.kind === "create") return `create item${d.itemCode ? ` ${d.itemCode}` : ""} + link supplier`;
  const oneField = (c: { field?: string; from?: unknown; to?: unknown; deltaPct?: number | null; reason?: string; tracking?: string }): string => {
    switch (c.field) {
      case "visibility": return `visibility → ${String(c.to)}${c.reason ? ` (${c.reason})` : ""}`;
      case "price": return `price ${money(c.from)} → ${money(c.to)}${c.deltaPct != null ? ` (${c.deltaPct > 0 ? "+" : ""}${c.deltaPct}%)` : ""}`;
      case "purchase": return `purchase ${money(c.from)} → ${money(c.to)}`;
      case "stock": return `stock ${String(c.from ?? 0)} → ${String(c.to ?? 0)}${c.tracking === "enabled" ? " (track)" : ""}`;
      case "weight": return `weight ${String(c.from ?? 0)}g → ${String(c.to ?? 0)}g`;
      case "ean": return `EAN ${String(c.from ?? "—")} → ${String(c.to ?? "")}`;
      case "supplier": return `supplier ${String(c.from ?? "—")} → ${String(c.to ?? "")}`;
      case "name": return `name ${String(c.from ?? "—")} → ${String(c.to ?? "")}`;
      case "brand": return `brand ${String(c.from ?? "—")} → ${String(c.to ?? "")}`;
      case "category": return `category + ${String(c.to ?? "")}`;
      default: return c.field ? `${c.field}: ${String(c.from)} → ${String(c.to)}` : "";
    }
  };
  if (Array.isArray(d.changes)) return d.changes.map(oneField).filter(Boolean).join(" · ") || "—";
  if (d.field) return oneField(d);
  const parts: string[] = [];
  if (d.action) parts.push(d.action);
  if (d.sales) parts.push(`sales ${money(d.sales.from)}→${money(d.sales.to)}`);
  if (d.purchase) parts.push(`purchase ${money(d.purchase.from)}→${money(d.purchase.to)}`);
  const labels: Record<string, string> = { Description: "description", SupplierItemCode: "supplier code" };
  const others = (d.fields ?? []).filter((f) => !["EndDate", "Status", "SalesPrice", "PurchasePrice", "StandardSalesPrice"].includes(f)).map((f) => labels[f] ?? f);
  if (others.length) parts.push(`sync ${others.join(", ")}`);
  return parts.join(" · ") || "—";
}

function statusBadge(status?: string | null) {
  if (!status) return null;
  const color = status === "applied" ? "green" : status === "failed" ? "red" : status === "skipped" ? "gray" : status === "dry_run" ? "blue" : "amber";
  return <Badge color={color}>{status}</Badge>;
}

function AuditTab() {
  const [userEmail, setUserEmail] = useState("");
  const [sku, setSku] = useState("");
  const [page, setPage] = useState(1);
  const [historySku, setHistorySku] = useState<string | null>(null);
  // Column filters — multi-select, applied in the query so paging counts matching rows only.
  const [fActions, setFActions] = useState<string[]>([]);
  const [fResults, setFResults] = useState<string[]>([]);
  const [fChanges, setFChanges] = useState<string[]>([]);
  const opts = trpc.activity.auditFilterOptions.useQuery(undefined, { staleTime: 60_000 });
  const filtersActive = fActions.length + fResults.length + fChanges.length > 0;
  const clearFilters = () => { setFActions([]); setFResults([]); setFChanges([]); setPage(1); };
  const q = trpc.activity.audit.useQuery({
    userEmail: userEmail || undefined,
    sku: sku || undefined,
    actions: fActions.length ? fActions : undefined,
    results: fResults.length ? fResults : undefined,
    changes: fChanges.length ? fChanges : undefined,
    page,
    pageSize: 50,
  });
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search SKU / item code…" value={sku} onChange={(e) => { setSku(e.target.value); setPage(1); }} className="w-48" />
        <Input placeholder="Filter user…" value={userEmail} onChange={(e) => { setUserEmail(e.target.value); setPage(1); }} className="w-44" />
        <Button variant="secondary" onClick={() => q.refetch()}>↻ Refresh</Button>
        {filtersActive && (
          <span className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-medium text-indigo-700">Filtered</span>
            {[...fActions, ...fChanges, ...fResults].map((v) => <Badge key={v} color="indigo">{v}</Badge>)}
            <button onClick={clearFilters} className="font-medium text-slate-500 underline hover:text-slate-800">clear all</button>
          </span>
        )}
      </div>
      <Card pad={false}>
        {q.isLoading ? <div className="p-5"><Spinner /></div> : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="p-3">When</th>
                <th>
                  <ColumnFilter
                    title="Action"
                    selected={fActions}
                    onChange={(v) => { setFActions(v); setPage(1); }}
                    options={(opts.data?.actions ?? []).map((a) => ({ value: a.value, label: a.value, count: a.count }))}
                  />
                </th>
                <th>Item</th>
                <th>
                  <ColumnFilter
                    title="What changed"
                    selected={fChanges}
                    onChange={(v) => { setFChanges(v); setPage(1); }}
                    options={(opts.data?.changes ?? []).map((c) => ({ value: c.value, label: CHANGE_LABELS[c.value] ?? c.value }))}
                  />
                </th>
                <th>
                  <ColumnFilter
                    title="Result"
                    selected={fResults}
                    onChange={(v) => { setFResults(v); setPage(1); }}
                    options={(opts.data?.results ?? []).map((r) => ({ value: r.value, label: r.value, count: r.count }))}
                  />
                </th>
                <th>User</th>
                <th className="p-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!q.data?.rows.length && (
                <tr>
                  <td colSpan={7} className="p-5 text-sm text-slate-500">
                    No transactions match.
                    {filtersActive && <> <button onClick={clearFilters} className="font-medium underline">Clear the filters</button> to see them all.</>}
                  </td>
                </tr>
              )}
              {(q.data?.rows ?? []).map((r) => (
                <tr key={r.id} className="align-top hover:bg-slate-50">
                  <td className="p-3 whitespace-nowrap text-slate-600">{fmtDateTime(r.at)}</td>
                  <td><Badge color="indigo">{r.action}</Badge></td>
                  <td className="whitespace-nowrap">
                    {r.sku ? (() => {
                      // LS ops key on the webshop article code but carry the PBS SKU + name in the diff —
                      // show the SKU (what the user manages by), keep history keyed on the op key (r.sku).
                      const dd = asObj(r.opDiff) as { sku?: string; desc?: string };
                      const label = dd.sku || r.sku!;
                      return (
                        <>
                          <button onClick={() => setHistorySku(r.sku!)} className="font-mono text-xs font-medium text-blue-600 hover:underline" title="View this item's full history">{label}</button>
                          {dd.sku && dd.sku !== r.sku && <span className="ml-1 text-[10px] text-slate-400">({r.sku})</span>}
                          {r.target && <span className="ml-1 text-[10px] uppercase text-slate-400">{r.target}</span>}
                          {dd.desc && <div className="max-w-[14rem] truncate text-[11px] text-slate-400" title={dd.desc}>{dd.desc}</div>}
                        </>
                      );
                    })() : (
                      <>
                        <span className="text-xs text-slate-400">{r.entity}{r.entityId ? ` #${r.entityId}` : ""}</span>
                        {r.target && <span className="ml-1 text-[10px] uppercase text-slate-400">{r.target}</span>}
                      </>
                    )}
                  </td>
                  <td className="text-slate-600">{r.sku ? summarizeDiff(r.opDiff, r.opType) : "—"}</td>
                  <td>{statusBadge(r.opStatus)}</td>
                  <td className="text-slate-500">{r.userEmail ?? "system"}</td>
                  <td className="p-3"><details><summary className="cursor-pointer text-xs text-indigo-600">view</summary><pre className="mt-1 max-w-md overflow-auto rounded bg-slate-50 p-2 text-[11px]">{JSON.stringify(r.after ?? r.before ?? {}, null, 2)}</pre></details></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Pager page={page} setPage={setPage} hasNext={(q.data?.rows.length ?? 0) === 50} />
      {historySku && <SkuHistoryModal sku={historySku} onClose={() => setHistorySku(null)} />}
    </>
  );
}

/** Full timeline for one SKU — every operation across change sets, with what changed + outcome. */
function SkuHistoryModal({ sku, onClose }: { sku: string; onClose: () => void }) {
  const h = trpc.activity.skuHistory.useQuery({ sku });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-800">History — <span className="font-mono">{sku}</span></h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="max-h-[72vh] overflow-auto p-5">
          {h.isLoading ? <Spinner /> : !h.data?.length ? <p className="text-sm text-slate-500">No operations recorded for this item yet.</p> : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                <tr><th className="py-2">When</th><th>Set</th><th>Target</th><th>What</th><th>Result</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {h.data.map((o) => (
                  <tr key={o.opId} className="align-top">
                    <td className="py-2 whitespace-nowrap text-slate-600">{fmtDateTime(o.appliedAt ?? o.csCreatedAt)}</td>
                    <td className="text-slate-500">#{o.changeSetId}</td>
                    <td className="text-xs uppercase text-slate-400">{o.target}</td>
                    <td className="text-slate-700">{summarizeDiff(o.diff, o.opType)}</td>
                    <td>{statusBadge(o.status)}{o.lastError && <div className="mt-0.5 max-w-xs break-words text-[11px] text-red-600">{o.lastError}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ApiTab() {
  const [target, setTarget] = useState<"all" | "exact" | "lightspeed" | "mcp" | "pbs_sftp">("all");
  const [okOnly, setOkOnly] = useState<"all" | "ok" | "failed">("all");
  const [endpoint, setEndpoint] = useState("");
  const [page, setPage] = useState(1);
  const q = trpc.activity.apiCalls.useQuery({ target, okOnly, endpoint: endpoint || undefined, page, pageSize: 50 });
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={target} onChange={(e) => { setTarget(e.target.value as typeof target); setPage(1); }}>
          <option value="all">All targets</option><option value="exact">ExactOnline</option><option value="lightspeed">Lightspeed</option><option value="mcp">MCP</option><option value="pbs_sftp">PBS FTP</option>
        </Select>
        <Select value={okOnly} onChange={(e) => { setOkOnly(e.target.value as typeof okOnly); setPage(1); }}>
          <option value="all">All results</option><option value="ok">OK only</option><option value="failed">Failed only</option>
        </Select>
        <Input placeholder="Filter endpoint…" value={endpoint} onChange={(e) => { setEndpoint(e.target.value); setPage(1); }} className="w-72" />
        <Button variant="secondary" onClick={() => q.refetch()}>↻ Refresh</Button>
      </div>
      <Card pad={false}>
        {q.isLoading ? <div className="p-5"><Spinner /></div> : !q.data?.rows.length ? <div className="p-5 text-sm text-slate-500">No API calls match.</div> : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
              <tr><th className="p-3">When</th><th>Target</th><th>Method</th><th>Endpoint</th><th>Status</th><th className="p-3 text-right">ms</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="p-3 whitespace-nowrap text-slate-600">{fmtDateTime(r.at)}</td>
                  <td>{r.target}</td>
                  <td className="font-mono text-xs">{r.method}</td>
                  <td className="max-w-md truncate font-mono text-xs text-slate-600">{r.endpoint}</td>
                  <td>{r.ok ? <Badge color="green">{r.statusCode ?? "ok"}</Badge> : <Badge color="red">{r.statusCode ?? "fail"}</Badge>}</td>
                  <td className="p-3 text-right text-slate-500">{r.durationMs ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Pager page={page} setPage={setPage} hasNext={(q.data?.rows.length ?? 0) === 50} />
    </>
  );
}

function Pager({ page, setPage, hasNext }: { page: number; setPage: (f: (p: number) => number) => void; hasNext: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Previous</Button>
      <span className="text-sm text-slate-500">Page {page}</span>
      <Button variant="secondary" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>Next →</Button>
    </div>
  );
}
